import {
  AdditiveBlending,
  Color,
  Group,
  InstancedMesh,
  Matrix4,
  Mesh,
  MeshStandardMaterial,
  Object3D,
  PointLight,
  Quaternion,
  RepeatWrapping,
  Sprite,
  SpriteMaterial,
  Vector3,
  type Scene,
  type Texture,
} from 'three';
import { clamp, damp, lerp } from '../utils/rng';
import { PITCH_MAX, PITCH_MIN } from '../utils/ballistics';
import { PLAY_BOUNDS, terrainHeight } from '../world/heightfield';
import { softSprite, trackLinks } from '../world/textures';
import { ParticleField } from './ParticleField';
import {
  DIMS,
  buildBarrel,
  buildHull,
  buildTrackBelt,
  buildTurret,
  buildWheel,
  wheelStations,
} from './tankGeometry';
import type { InputState } from '../controls/Input';

/**
 * Le char : conduite, assiette sur le terrain, pointage de la tourelle,
 * defilement des chenilles, recul et poussiere.
 *
 * La hierarchie est volontairement plate et rigide :
 *
 *     root            position et cap, redresse sur le terrain
 *     +- hull         caisse, jupes, echappements  (fixe)
 *     +- tracks[2]    chenilles                    (UV qui defilent)
 *     +- wheels[2]    galets                        (tournent)
 *     +- turret       tourelle                      (gisement)
 *        +- barrel    tube                          (site)
 *           +- muzzle point de depart de l'obus     (vide)
 *
 * Le point de depart de l'obus et sa direction sont *lus* dans la matrice
 * monde du noeud `muzzle` au lieu d'etre recalcules a la main. Il n'y a donc
 * aucun moyen que le canon vise ailleurs que la ou l'obus part.
 * cf. doc/05-char-et-chenilles.md
 */

const FORWARD_SPEED = 11.5;
const REVERSE_SPEED = 5.5;
/** Vitesse de rotation de la caisse a l'arret, en rad/s. */
const TURN_RATE = 0.95;

/**
 * Vitesses de pointage, en rad/s. Un char ne vise pas a la vitesse de la
 * souris : la tourelle pese quinze tonnes. On lit donc la souris comme une
 * *consigne* (ou le joueur veut viser) et la tourelle la rejoint a vitesse
 * bornee. C'est ce decalage qui donne la sensation de masse.
 */
const TRAVERSE_RATE = 0.9;
const ELEVATE_RATE = 0.5;

/** Course du tube au recul, en metres. */
const RECOIL_STROKE = 0.55;

/**
 * Avance maximale de la consigne sur le tube, en radians.
 *
 * La tourelle rejoint la consigne a vitesse bornee : si la consigne part
 * beaucoup trop loin, le tube continue de tourner pendant plusieurs secondes
 * apres que le joueur a lache la souris. On borne donc l'ecart a ce que la
 * tourelle peut rattraper en une demi-seconde environ. Sans ca, une seule
 * frame longue -- ou un pic de verrouillage du pointeur -- suffit a envoyer
 * le canon a la butee.
 */
const MAX_AIM_LEAD = 0.4;

/** Axe transversal du char : celui autour duquel tournent les galets. */
const AXLE = new Vector3(1, 0, 0);

export class Tank {
  readonly root = new Group();
  readonly position = new Vector3();

  /** Cap de la caisse, en radians. 0 regarde vers +z. */
  hullYaw = 0;
  /** Gisement de la tourelle, *relatif a la caisse*. */
  turretYaw = 0;
  /** Site du tube, relatif a la caisse. C'est l'angle mecanique du canon. */
  gunPitch = 0;

  speed = 0;

  private turretTarget = 0;
  private gunTarget = 0;

  private readonly turret = new Group();
  private readonly barrel = new Group();
  private readonly barrelMesh: Mesh;
  private readonly muzzle = new Object3D();

  private readonly trackTextures: Texture[] = [];
  private readonly wheels: InstancedMesh[] = [];
  private readonly wheelSpin = [0, 0];
  private readonly trackTile: number;
  private readonly stations = wheelStations();

  private readonly flash: Sprite;
  private readonly flashLight: PointLight;
  private flashTime = 0;

  private recoil = 0;
  /** Cabrage de la caisse au depart du coup, qui s'amortit. */
  private kick = 0;

  private readonly dust: ParticleField;
  private dustDebt = 0;

  private pitch = 0;
  private roll = 0;
  private readonly tmpQ = new Quaternion();
  private readonly tmpV = new Vector3();
  private readonly scratchM = new Matrix4();
  private readonly scratchQ = new Quaternion();
  private readonly scratchPos = new Vector3();
  private readonly scratchScl = new Vector3();
  private readonly dustColor = new Color(0x6d5f45);
  private readonly dustPos = new Vector3();
  private readonly dustVel = new Vector3();

  constructor(scene: Scene) {
    const hullMat = new MeshStandardMaterial({
      color: 0x4d5440,
      roughness: 0.78,
      metalness: 0.18,
      flatShading: true,
    });
    const hull = new Mesh(buildHull(), hullMat);
    hull.castShadow = true;
    hull.receiveShadow = true;
    this.root.add(hull);

    const turretMesh = new Mesh(buildTurret(), hullMat);
    turretMesh.castShadow = true;
    turretMesh.receiveShadow = true;
    this.turret.position.set(0, DIMS.hullTop, DIMS.turretZ);
    this.turret.add(turretMesh);
    this.root.add(this.turret);

    this.barrelMesh = new Mesh(
      buildBarrel(),
      new MeshStandardMaterial({
        color: 0x434a3b,
        roughness: 0.55,
        metalness: 0.4,
        flatShading: true,
      }),
    );
    this.barrelMesh.castShadow = true;
    // Le tourillon est exprime dans le repere du char ; la tourelle est deja
    // decalee, on retranche donc son offset.
    this.barrel.position.set(0, DIMS.trunnionY - DIMS.hullTop, DIMS.trunnionZ - DIMS.turretZ);
    this.barrel.add(this.barrelMesh);
    this.muzzle.position.set(0, 0, DIMS.barrelLength);
    this.barrel.add(this.muzzle);
    this.turret.add(this.barrel);

    this.trackTile = this.buildTracks();
    this.buildWheels();

    // Eclair de bouche : un sprite additif, plus une lumiere ponctuelle. La
    // lumiere ne sert qu'a eclairer le canon et le sol pendant 60 ms, mais
    // c'est elle qui fait lire « depart du coup » plutot que « sprite ».
    this.flash = new Sprite(
      new SpriteMaterial({
        map: softSprite(),
        color: 0xffd79a,
        transparent: true,
        blending: AdditiveBlending,
        depthWrite: false,
      }),
    );
    this.flash.visible = false;
    scene.add(this.flash);

    this.flashLight = new PointLight(0xffc06a, 0, 40, 2);
    scene.add(this.flashLight);

    this.dust = new ParticleField(scene, {
      capacity: 320,
      texture: softSprite(),
      blend: 'additive',
      gravity: -1.4,
      drag: 1.1,
      fadePower: 1.6,
    });

    scene.add(this.root);
  }

  /** Construit les deux chenilles et renvoie la periode de leur texture. */
  private buildTracks(): number {
    const belt = buildTrackBelt();
    const base = trackLinks();

    for (const side of [-1, 1]) {
      // Chaque chenille a *sa* texture : en virage sur place les deux brins
      // defilent en sens inverse, et une texture partagee ne pourrait pas le
      // montrer.
      const tex = side < 0 ? base : base.clone();
      tex.wrapS = RepeatWrapping;
      tex.wrapT = RepeatWrapping;
      tex.needsUpdate = true;
      this.trackTextures.push(tex);

      const mesh = new Mesh(
        belt.geometry,
        new MeshStandardMaterial({
          map: tex,
          roughness: 0.72,
          metalness: 0.45,
          flatShading: true,
        }),
      );
      mesh.position.x = side * DIMS.trackOffsetX;
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      this.root.add(mesh);
    }
    return belt.tile;
  }

  private buildWheels(): void {
    const mat = new MeshStandardMaterial({
      color: 0x2f302b,
      roughness: 0.8,
      metalness: 0.3,
      flatShading: true,
    });
    // Une seule geometrie de rayon 1, mise a l'echelle par instance :
    // barbotin, tendeur et galets partagent le meme maillage.
    const geo = buildWheel();

    for (const side of [-1, 1]) {
      const mesh = new InstancedMesh(geo, mat, this.stations.length);
      mesh.castShadow = true;
      mesh.position.x = side * DIMS.trackOffsetX;
      this.wheels.push(mesh);
      this.root.add(mesh);
    }
  }

  spawn(at: Vector3, yaw: number): void {
    this.position.copy(at);
    this.position.y = terrainHeight(at.x, at.z);
    this.hullYaw = yaw;
    this.turretYaw = 0;
    this.turretTarget = 0;
    this.gunPitch = 0;
    this.gunTarget = 0;
    this.speed = 0;
    this.recoil = 0;
    this.kick = 0;
    this.pitch = 0;
    this.roll = 0;
    this.dust.reset();
    this.root.position.copy(this.position);
    this.settle(0, true);
    this.root.updateMatrixWorld(true);
  }

  update(dt: number, input: InputState): void {
    this.drive(dt, input);
    this.aim(dt, input);

    // Recul : la course est prise d'un coup au depart du coup (20 ms sur un
    // vrai canon) puis le recuperateur ramene le tube lentement.
    this.recoil = damp(this.recoil, 0, 7, dt);
    this.kick = damp(this.kick, 0, 9, dt);
    this.barrelMesh.position.z = -RECOIL_STROKE * this.recoil;

    this.flashTime = Math.max(0, this.flashTime - dt);
    this.updateFlash();

    this.dust.update(dt);
    this.root.updateMatrixWorld(true);
  }

  // --- conduite ---

  private drive(dt: number, input: InputState): void {
    const speedFrac = clamp(Math.abs(this.speed) / FORWARD_SPEED, 0, 1);
    // Un char braque moins vite lance qu'a l'arret : les chenilles patinent.
    const omega = input.turn * TURN_RATE * (1 - 0.45 * speedFrac);
    this.hullYaw += omega * dt;
    // La tourelle garde son gisement *monde* quand la caisse pivote : c'est
    // tout l'interet d'une tourelle. On compense donc la rotation de caisse.
    this.turretTarget -= omega * dt;
    this.turretYaw -= omega * dt;

    const cruise = input.forward >= 0 ? FORWARD_SPEED : REVERSE_SPEED;
    this.speed = damp(this.speed, input.forward * cruise, 2.2, dt);

    const dirX = Math.sin(this.hullYaw);
    const dirZ = Math.cos(this.hullYaw);
    const step = this.speed * dt;

    // Penalite de pente : grimper un merlon est lent, ce qui dissuade d'y
    // monter sans mur invisible.
    const probe = Math.max(Math.abs(step), 0.4);
    const nx = this.position.x + dirX * probe * Math.sign(step || 1);
    const nz = this.position.z + dirZ * probe * Math.sign(step || 1);
    const grade = (terrainHeight(nx, nz) - this.position.y) / probe;
    const factor = clamp(1 - grade * 1.5, 0.18, 1.2);

    this.position.x = clamp(this.position.x + dirX * step * factor, PLAY_BOUNDS.minX, PLAY_BOUNDS.maxX);
    this.position.z = clamp(this.position.z + dirZ * step * factor, PLAY_BOUNDS.minZ, PLAY_BOUNDS.maxZ);

    this.settle(dt, false);
    this.spinTracks(dt, omega);
    this.raiseDust(dt, omega);
  }

  /**
   * Assiette : on echantillonne le sol aux *quatre coins* de l'empreinte des
   * chenilles, et on en deduit un tangage et un roulis.
   *
   * Une seule normale de terrain -- ce que fait la licorne du projet jumeau --
   * suffit pour un animal de deux metres. Un char en fait sept : sur une levre
   * de cratere, la normale au centre de gravite ne dit rien de ce sur quoi le
   * vehicule repose vraiment, et le char se met a trembler. Quatre points, un
   * plan moyen : c'est la version la plus simple d'une suspension.
   */
  private settle(dt: number, immediate: boolean): void {
    const bx = DIMS.trackOffsetX;
    const bz = DIMS.trackLength / 2 - 0.5;
    const s = Math.sin(this.hullYaw);
    const c = Math.cos(this.hullYaw);

    // Les quatre coins, du repere du char vers le monde.
    const h = (lx: number, lz: number): number =>
      terrainHeight(this.position.x + lx * c + lz * s, this.position.z - lx * s + lz * c);

    const fl = h(-bx, bz);
    const fr = h(bx, bz);
    const rl = h(-bx, -bz);
    const rr = h(bx, -bz);

    const front = (fl + fr) / 2;
    const rear = (rl + rr) / 2;
    const left = (fl + rl) / 2;
    const right = (fr + rr) / 2;

    // Nez en l'air = rotation negative autour de X, cf. Rx(t) qui envoie +z
    // sur (0, -sin t, cos t).
    const pitchTarget = -Math.atan2(front - rear, 2 * bz);
    const rollTarget = -Math.atan2(left - right, 2 * bx);

    // Entre la moyenne et le maximum : la moyenne enterre le char dans un
    // cratere, le maximum le fait sauter sur chaque caillou.
    const avg = (fl + fr + rl + rr) / 4;
    const top = Math.max(fl, fr, rl, rr);
    const yTarget = lerp(avg, top, 0.5);

    if (immediate) {
      this.pitch = pitchTarget;
      this.roll = rollTarget;
      this.position.y = yTarget;
    } else {
      this.pitch = damp(this.pitch, pitchTarget, 9, dt);
      this.roll = damp(this.roll, rollTarget, 9, dt);
      this.position.y = damp(this.position.y, yTarget, 12, dt);
    }

    this.root.position.copy(this.position);
    // Ordre YXZ : cap, puis tangage, puis roulis -- l'ordre d'un vehicule.
    this.root.rotation.set(this.pitch + this.kick * 0.055, this.hullYaw, this.roll, 'YXZ');
  }

  /**
   * Defilement des chenilles et rotation des galets.
   *
   * La vitesse de chaque chenille est celle du centre du char plus ou moins
   * la contribution du braquage : v +/- omega * b. A l'arret en train de
   * pivoter, les deux chenilles tournent en sens inverse, exactement comme un
   * vrai char.
   */
  private spinTracks(dt: number, omega: number): void {
    const b = DIMS.trackOffsetX;
    for (let i = 0; i < 2; i++) {
      const side = i === 0 ? -1 : 1;
      const v = this.speed + omega * b * side;
      const distance = v * dt;
      const tex = this.trackTextures[i];
      // La texture est echantillonnee en v + offset : pour que le motif
      // recule quand le char avance, l'offset doit croitre.
      if (tex) tex.offset.y += distance / this.trackTile;
      this.wheelSpin[i] = (this.wheelSpin[i] ?? 0) + distance;
    }
    this.layWheels();
  }

  private layWheels(): void {
    this.wheels.forEach((mesh, i) => {
      // `wheelSpin` est une distance parcourue ; chaque galet la convertit en
      // angle avec *son* rayon, sinon le barbotin tournerait aussi vite que
      // les petits galets.
      const distance = this.wheelSpin[i] ?? 0;
      this.stations.forEach((st, k) => {
        this.scratchPos.set(0, st.y, st.z);
        this.scratchQ.setFromAxisAngle(AXLE, distance / st.radius);
        // Largeur conservee (geometrie de rayon 1, 0,76 m d'epaisseur) : on ne
        // met a l'echelle que le plan du galet.
        this.scratchScl.set(1, st.radius, st.radius);
        this.scratchM.compose(this.scratchPos, this.scratchQ, this.scratchScl);
        mesh.setMatrixAt(k, this.scratchM);
      });
      mesh.instanceMatrix.needsUpdate = true;
    });
  }

  /** Poussiere soulevee par les chenilles, proportionnelle a leur vitesse. */
  private raiseDust(dt: number, omega: number): void {
    const b = DIMS.trackOffsetX;
    const activity = Math.max(
      Math.abs(this.speed + omega * b),
      Math.abs(this.speed - omega * b),
    );
    if (activity < 1.2) {
      this.dustDebt = 0;
      return;
    }
    // Un compteur fractionnaire : le debit reste correct a 30 comme a 144 Hz.
    this.dustDebt += activity * 5 * dt;
    const s = Math.sin(this.hullYaw);
    const c = Math.cos(this.hullYaw);

    while (this.dustDebt >= 1) {
      this.dustDebt -= 1;
      const side = Math.random() < 0.5 ? -1 : 1;
      // Derriere la chenille : c'est la que la terre est rejetee.
      const lx = side * b + (Math.random() - 0.5) * 0.5;
      const lz = -DIMS.trackLength / 2 + Math.random() * 1.2;
      const wx = this.position.x + lx * c + lz * s;
      const wz = this.position.z - lx * s + lz * c;
      this.dustPos.set(wx, terrainHeight(wx, wz) + 0.2, wz);
      this.dustVel.set(
        (Math.random() - 0.5) * 1.6 - Math.sin(this.hullYaw) * this.speed * 0.12,
        0.7 + Math.random() * 1.3,
        (Math.random() - 0.5) * 1.6 - Math.cos(this.hullYaw) * this.speed * 0.12,
      );
      this.dust.spawn(this.dustPos, this.dustVel, 0.9 + Math.random() * 0.9, this.dustColor, 2.2);
    }
  }

  // --- pointage ---

  private aim(dt: number, input: InputState): void {
    // Souris et clavier alimentent la meme consigne.
    this.turretTarget -= input.mouseYaw + input.traverse * TRAVERSE_RATE * dt;
    this.gunTarget = clamp(
      this.gunTarget - input.mousePitch + input.elevate * ELEVATE_RATE * dt,
      PITCH_MIN,
      PITCH_MAX,
    );

    // La consigne ne peut pas s'echapper : elle reste a portee de rattrapage.
    this.turretTarget = clamp(
      this.turretTarget,
      this.turretYaw - MAX_AIM_LEAD,
      this.turretYaw + MAX_AIM_LEAD,
    );
    this.gunTarget = clamp(this.gunTarget, this.gunPitch - MAX_AIM_LEAD, this.gunPitch + MAX_AIM_LEAD);

    // La tourelle rejoint la consigne a vitesse bornee.
    this.turretYaw = slew(this.turretYaw, this.turretTarget, TRAVERSE_RATE * dt);
    this.gunPitch = slew(this.gunPitch, this.gunTarget, ELEVATE_RATE * dt);

    this.turret.rotation.y = this.turretYaw;
    // Rx(-site) : pointer en l'air est une rotation negative autour de X.
    this.barrel.rotation.x = -this.gunPitch;
  }

  /**
   * Pointe le tube dans une direction *monde* donnee.
   *
   * C'est la conversion qui manquait, et elle n'est pas cosmetique : le canon
   * est pointe par rapport a la *caisse*, et la caisse epouse le terrain. Une
   * solution de tir calculee dans le monde (c'est le cas de toutes : la
   * gravite est verticale) doit donc etre ramenee dans le repere du char
   * avant d'etre appliquee. Deux degres d'assiette non compenses, et l'obus
   * tombe dix-sept metres a cote a 70 m.
   *
   * On passe par la matrice monde plutot que par de la trigonometrie : la
   * caisse a un cap, un tangage et un roulis, et les composer a la main est
   * exactement le genre de calcul qu'on se trompe a ecrire.
   */
  layGunWorld(direction: Vector3): void {
    this.root.updateMatrixWorld(true);
    // Direction voulue, exprimee dans le repere de la caisse.
    const local = this.tmpV
      .copy(direction)
      .normalize()
      .applyQuaternion(this.tmpQ.copy(this.root.quaternion).invert());
    // La chaine tourelle (Ry) puis tube (Rx) envoie +z sur
    // (cos p sin t, sin p, cos p cos t) : les deux angles se lisent
    // directement.
    this.layGun(Math.atan2(local.x, local.z), Math.asin(clamp(local.y, -1, 1)));
  }

  /**
   * Pointe instantanement la tourelle et le canon, consigne comprise.
   *
   * C'est le seul moyen de court-circuiter la vitesse de pointage, et il
   * n'existe que pour les hooks de developpement et les tests : sans lui,
   * verifier une portee demanderait d'attendre plusieurs secondes que la
   * tourelle ait fini de tourner. Les angles sont relatifs a la caisse.
   */
  layGun(turretYaw: number, gunPitch: number): void {
    const pitch = clamp(gunPitch, PITCH_MIN, PITCH_MAX);
    this.turretYaw = turretYaw;
    this.turretTarget = turretYaw;
    this.gunPitch = pitch;
    this.gunTarget = pitch;
    this.turret.rotation.y = this.turretYaw;
    this.barrel.rotation.x = -this.gunPitch;
    this.root.updateMatrixWorld(true);
  }

  /**
   * Remplit le point de depart et la direction de l'obus, puis declenche le
   * recul.
   *
   * Les deux operations sont dans le meme appel a dessein : le recul recule la
   * bouche de 55 cm, et lire la bouche *apres* l'avoir reculee ferait partir
   * l'obus de l'interieur du masque de canon.
   */
  fire(outPoint: Vector3, outDir: Vector3): void {
    this.root.updateMatrixWorld(true);
    this.muzzle.getWorldPosition(outPoint);
    outDir.set(0, 0, 1).applyQuaternion(this.barrel.getWorldQuaternion(this.tmpQ)).normalize();

    this.recoil = 1;
    this.kick = 1;
    this.flashTime = 0.075;
    this.updateFlash();
  }

  private updateFlash(): void {
    const on = this.flashTime > 0;
    this.flash.visible = on;
    if (!on) {
      this.flashLight.intensity = 0;
      return;
    }
    // La bouche a recule : l'eclair la suit.
    this.muzzle.getWorldPosition(this.tmpV);
    this.flash.position.copy(this.tmpV);
    const k = this.flashTime / 0.075;
    this.flash.scale.setScalar(lerp(1.6, 5.2, k));
    this.flash.material.opacity = k;
    this.flashLight.position.copy(this.tmpV);
    this.flashLight.intensity = 900 * k;
  }

  // --- lecture ---

  /** Point de depart de l'obus, en coordonnees monde. */
  muzzlePoint(out: Vector3): Vector3 {
    return this.muzzle.getWorldPosition(out);
  }

  /** Direction de tir, en coordonnees monde. */
  muzzleDirection(out: Vector3): Vector3 {
    return out.set(0, 0, 1).applyQuaternion(this.barrel.getWorldQuaternion(this.tmpQ)).normalize();
  }

  /**
   * Site *reel* du tube, mesure dans le monde.
   *
   * Il differe de `gunPitch` : le canon est pointe par rapport a la caisse, et
   * la caisse suit le terrain. Sur une pente a 5 degres, une hausse mecanique
   * de 10 degres donne 15 degres de site -- et c'est le site reel qui decide
   * de la portee. C'est celui-la que le HUD affiche.
   */
  get worldPitch(): number {
    this.muzzleDirection(this.tmpV);
    return Math.asin(clamp(this.tmpV.y, -1, 1));
  }

  /** Gisement reel du tube, en coordonnees monde. */
  get worldYaw(): number {
    this.muzzleDirection(this.tmpV);
    return Math.atan2(this.tmpV.x, this.tmpV.z);
  }

  get speedRatio(): number {
    return clamp(Math.abs(this.speed) / FORWARD_SPEED, 0, 1);
  }

  /** Vrai tant que la tourelle n'a pas rejoint la consigne du joueur. */
  get slewing(): boolean {
    return Math.abs(this.turretYaw - this.turretTarget) > 0.004;
  }

  /** Centre approximatif de la tourelle, pour y accrocher la camera. */
  turretAnchor(out: Vector3): Vector3 {
    return this.turret.getWorldPosition(out);
  }

  /** Axe de rotation de la tourelle vu du monde, pour orienter la camera. */
  get worldTurretYaw(): number {
    return this.hullYaw + this.turretYaw;
  }

  dispose(): void {
    this.dust.dispose();
  }
}

/** Rapproche `current` de `target` d'au plus `maxStep`. */
function slew(current: number, target: number, maxStep: number): number {
  const delta = target - current;
  if (Math.abs(delta) <= maxStep) return target;
  return current + Math.sign(delta) * maxStep;
}
